/**
 * OAuth2 password-grant auth for BeatDuel SPA.
 * Tokens stored in localStorage. 401 triggers auto-logout.
 */

import { navigate, navigateReplace } from './router.svelte';
import { trackEvent } from './analytics.svelte';
import { loadExperiments, resetExperiments } from './experiments.svelte';
import { consumeReturnTo } from './utils/returnTo';

const TOKEN_KEY = 'beatduel_token';
const REFRESH_KEY = 'beatduel_refresh_token';
const USER_KEY = 'beatduel_user';
const CLIENT_ID = 'beatduel-spa';
const CLIENT_SECRET = 'beatduel-secret';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  avatar_url: string | null;
  is_admin: boolean;
  email_verified?: boolean;
  spotify_id?: string | null;
  apple_id?: string | null;
  apple_music_connected_at?: string | null;
  spotify_connected_at?: string | null;
  soundcloud_user_id?: number | null;
  tiktok_id?: string | null;
  subscription_tier?: string;
  onboarding_complete?: boolean;
  privacy_version_accepted?: string | null;
  default_username?: boolean;
}

let _token = $state<string | null>(localStorage.getItem(TOKEN_KEY));
let _user = $state<AuthUser | null>(loadUser());

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return _token;
}

export function getUser(): AuthUser | null {
  return _user;
}

export function isLoggedIn(): boolean {
  return _token !== null;
}

export async function login(username: string, password: string): Promise<void> {
  const res = await fetch('/api/oauth2/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'password',
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      username,
      password,
    }),
  });

  if (!res.ok) {
    // OAuth rejects banned users with generic "invalid credentials". Call /auth/ban-check
    // to distinguish wrong-password from banned so the UI can explain why access is denied.
    try {
      const banRes = await fetch('/api/v1/auth/ban-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (banRes.ok) {
        const banRaw = await banRes.json();
        const banData = banRaw && banRaw.success && banRaw.data ? banRaw.data : banRaw;
        if (banData?.banned === true) {
          const err: Error & { banReason?: string } = new Error('Account restricted');
          err.banReason = typeof banData.reason === 'string' ? banData.reason : '';
          throw err;
        }
      }
    } catch (banErr) {
      if ((banErr as Error & { banReason?: string })?.banReason !== undefined) {
        throw banErr;
      }
      // Ban-check itself failed (rate limit, network, etc.) — fall through to generic error.
    }

    const errRaw = await res.json().catch(() => ({}));
    const errData = errRaw?.error || errRaw;
    throw new Error(errData.message || errData.error_description || 'Login failed');
  }

  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  _token = data.access_token;
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) {
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
  }

  // Fetch current user profile
  await fetchCurrentUser();
}

export async function fetchCurrentUser(): Promise<void> {
  const res = await fetch('/api/v1/users/current', {
    headers: { Authorization: `Bearer ${_token}` },
  });

  if (!res.ok) {
    logout();
    return;
  }

  const raw = await res.json();
  // Unwrap envelope if present: {success: true, data: {user: ...}} → {user: ...}
  const data = raw && raw.success && raw.data ? raw.data : raw;
  _user = data.user;
  localStorage.setItem(USER_KEY, JSON.stringify(_user));
  loadExperiments();
}

export function logout(): void {
  trackEvent('logout');
  // Revoke token server-side (best-effort)
  if (_token) {
    fetch('/api/v1/auth/revoke-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${_token}` },
      body: JSON.stringify({ token: _token }),
    }).catch(() => {});
  }
  _token = null;
  _user = null;
  resetExperiments();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  navigate('/login');
}

/** Attempt to refresh the access token using the stored refresh token. Returns true on success. */
export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;

  try {
    const res = await fetch('/api/oauth2/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        refresh_token: refreshToken,
      }),
    });

    if (!res.ok) return false;

    const raw = await res.json();
    const data = raw && raw.success && raw.data ? raw.data : raw;
    _token = data.access_token;
    localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_KEY, data.refresh_token);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Handle TikTok OAuth callback.
 * TikTok redirects to /auth/tiktok-callback?code=xxx&state=yyy.
 * We exchange the code for a BeatDuel access token via the API.
 */
export async function handleTikTokCallback(): Promise<{ success: boolean; isNew?: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  if (!code) {
    return { success: false, error: params.get('error_description') || params.get('error') || 'No authorization code' };
  }

  // Validate CSRF state
  const state = params.get('state');
  const savedState = sessionStorage.getItem('tiktok_oauth_state');
  sessionStorage.removeItem('tiktok_oauth_state');
  if (!state || state !== savedState) {
    return { success: false, error: 'Invalid OAuth state' };
  }

  // Invite code carried over from the Register landing page (if user came via #/register?code=xyz).
  const inviteCode = sessionStorage.getItem('oauth_invite_code') || undefined;
  sessionStorage.removeItem('oauth_invite_code');

  const res = await fetch('/api/v1/auth/tiktok/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, invite_code: inviteCode }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { success: false, error: data.message || 'TikTok login failed' };
  }

  const data = await res.json();
  _token = data.access_token;
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  await fetchCurrentUser();

  return { success: true, isNew: data.is_new };
}

/**
 * Start TikTok OAuth flow — fetch auth URL and redirect.
 * Pass an inviteCode when initiating from the invite landing page so it can
 * be redeemed in the callback (the OAuth provider can't relay this for us,
 * so it lives in sessionStorage across the redirect alongside the CSRF state).
 */
export async function startTikTokLogin(inviteCode?: string): Promise<string> {
  const res = await fetch('/api/v1/auth/tiktok/url');
  if (!res.ok) throw new Error('Could not get TikTok login URL');
  const { url, state } = await res.json();
  sessionStorage.setItem('tiktok_oauth_state', state);
  if (inviteCode) sessionStorage.setItem('oauth_invite_code', inviteCode);
  return url;
}

/**
 * Handle Google OAuth callback.
 * Google redirects to /auth/google-callback?code=xxx&state=yyy.
 * We exchange the code for a BeatDuel access token via the API, then route
 * brand-new users (or anyone who hasn't accepted privacy yet) into onboarding.
 */
export async function handleGoogleCallback(): Promise<{ success: boolean; isNew?: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  if (!code) {
    return {
      success: false,
      error: params.get('error_description') || params.get('error') || 'No authorization code',
    };
  }

  const state = params.get('state');
  const savedState = sessionStorage.getItem('google_oauth_state');
  sessionStorage.removeItem('google_oauth_state');
  if (!state || state !== savedState) {
    return { success: false, error: 'Invalid OAuth state' };
  }

  const inviteCode = sessionStorage.getItem('oauth_invite_code') || undefined;
  sessionStorage.removeItem('oauth_invite_code');

  const res = await fetch('/api/v1/auth/google/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, invite_code: inviteCode }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = data?.error?.message || data?.message || 'Google login failed';
    return { success: false, error: err };
  }

  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  _token = data.access_token;
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  await fetchCurrentUser();

  return { success: true, isNew: data.is_new };
}

/**
 * Start Google OAuth flow — fetch auth URL and redirect.
 * Pass an inviteCode when initiating from the invite landing page so it can
 * be redeemed in the callback. Stored in sessionStorage across the redirect.
 */
export async function startGoogleLogin(inviteCode?: string): Promise<string> {
  const res = await fetch('/api/v1/auth/google/url');
  if (!res.ok) throw new Error('Could not get Google login URL');
  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  if (!data.url) throw new Error('Google sign-in is not configured');
  sessionStorage.setItem('google_oauth_state', data.state);
  if (inviteCode) sessionStorage.setItem('oauth_invite_code', inviteCode);
  return data.url;
}

/**
 * Start Spotify OAuth flow — fetch auth URL and redirect.
 * Pass an inviteCode when initiating from the invite landing page so it can
 * be redeemed in the callback. Stored in sessionStorage across the redirect.
 */
export async function startSpotifyLogin(inviteCode?: string): Promise<string> {
  const res = await fetch('/api/v1/auth/spotify/login-url');
  if (!res.ok) throw new Error('Could not get Spotify login URL');
  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  if (!data.url) throw new Error('Spotify sign-in is not configured');
  sessionStorage.setItem('spotify_login_state', data.state);
  if (inviteCode) sessionStorage.setItem('oauth_invite_code', inviteCode);
  return data.url;
}

/**
 * Handle Spotify OAuth callback.
 * Spotify redirects to /auth/spotify-login-callback?code=xxx&state=yyy.
 * We exchange the code for a BeatDuel access token via the API, then route
 * brand-new users (or anyone who hasn't accepted the current privacy version)
 * into onboarding so the Welcome step can capture DOB + privacy consent.
 */
export async function handleSpotifyLoginCallback(): Promise<{ success: boolean; isNew?: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    return { success: false, error: params.get('error_description') || params.get('error') || 'No authorization code' };
  }
  const state = params.get('state');
  const savedState = sessionStorage.getItem('spotify_login_state');
  sessionStorage.removeItem('spotify_login_state');
  if (!state || state !== savedState) {
    return { success: false, error: 'Invalid OAuth state' };
  }
  const inviteCode = sessionStorage.getItem('oauth_invite_code') || undefined;
  sessionStorage.removeItem('oauth_invite_code');

  const res = await fetch('/api/v1/auth/spotify/login-callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, invite_code: inviteCode }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = data?.error?.message || data?.message || 'Spotify login failed';
    return { success: false, error: err };
  }
  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  _token = data.access_token;
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  await fetchCurrentUser();
  return { success: true, isNew: data.is_new };
}

/**
 * Start Apple OAuth flow — fetch auth URL and redirect.
 * Pass an inviteCode when initiating from the invite landing page so it can
 * be redeemed in the callback. Stored in sessionStorage across the redirect.
 */
export async function startAppleLogin(inviteCode?: string): Promise<string> {
  const res = await fetch('/api/v1/auth/apple/url');
  if (!res.ok) throw new Error('Could not get Apple login URL');
  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  if (!data.url) throw new Error('Apple sign-in is not configured');
  sessionStorage.setItem('apple_oauth_state', data.state);
  if (inviteCode) sessionStorage.setItem('oauth_invite_code', inviteCode);
  return data.url;
}

/**
 * Handle Apple OAuth callback.
 * Apple redirects to /auth/apple-callback?code=xxx&state=yyy&user=zzz.
 * We exchange the code for a BeatDuel access token via the API, then route
 * brand-new users (or anyone who hasn't accepted the current privacy version)
 * into onboarding so the Welcome step can capture DOB + privacy consent.
 */
export async function handleAppleCallback(): Promise<{ success: boolean; isNew?: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    return { success: false, error: params.get('error_description') || params.get('error') || 'No authorization code' };
  }
  const state = params.get('state');
  const savedState = sessionStorage.getItem('apple_oauth_state');
  sessionStorage.removeItem('apple_oauth_state');
  if (!state || state !== savedState) {
    return { success: false, error: 'Invalid OAuth state' };
  }
  const userPayload = params.get('user') || undefined;
  const inviteCode = sessionStorage.getItem('oauth_invite_code') || undefined;
  sessionStorage.removeItem('oauth_invite_code');

  const res = await fetch('/api/v1/auth/apple/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, user: userPayload, invite_code: inviteCode }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = data?.error?.message || data?.message || 'Apple login failed';
    return { success: false, error: err };
  }
  const raw = await res.json();
  const data = raw && raw.success && raw.data ? raw.data : raw;
  _token = data.access_token;
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  await fetchCurrentUser();
  return { success: true, isNew: data.is_new };
}

/**
 * Spotify CONNECT (authenticated link), distinct from Spotify LOGIN. Returns to
 * a sessionStorage-stored destination (onboarding or account). Uses raw fetch
 * with the stored bearer token (auth.svelte.ts is the bootstrap exception to
 * the api-client rule).
 */
export async function handleSpotifyConnectCallback(): Promise<{ success: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    return { success: false, error: params.get('error') || 'No authorization code' };
  }
  const state = params.get('state');
  const savedState = sessionStorage.getItem('spotify_connect_state');
  sessionStorage.removeItem('spotify_connect_state');
  if (!state || state !== savedState) {
    return { success: false, error: 'Invalid OAuth state' };
  }
  const res = await fetch('/api/v1/auth/spotify/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${_token}` },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { success: false, error: data?.error?.message || data?.message || 'Spotify connect failed' };
  }
  await fetchCurrentUser();
  return { success: true };
}

/**
 * Route an OAuth callback failure to the right SPA page.
 * Invite-related errors (`invite_required:` / `invite_invalid:` message
 * prefixes, set by InviteService) bounce back to /#/register so the user can
 * grab a fresh invite. Other errors stay on /#/login.
 */
function routeOAuthError(error: string, loginErrorKey: string): void {
  if (error.startsWith('invite_required:') || error.startsWith('invite_invalid:')) {
    sessionStorage.setItem('register_invite_error', error);
    navigateReplace('/register');
    return;
  }
  sessionStorage.setItem(loginErrorKey, error);
  navigateReplace('/login');
}

/**
 * Initialize auth on app start.
 * If we have a stored token, validate it by fetching current user.
 * Also handles TikTok OAuth callback redirect.
 */
export async function initAuth(): Promise<void> {
  // Handle TikTok OAuth callback if on that path
  if (window.location.pathname === '/auth/tiktok-callback') {
    const result = await handleTikTokCallback();
    // Clean the URL and redirect to SPA. A brand-new user (or anyone
    // who hasn't completed onboarding) must go through onboarding first —
    // the stashed return-to (if any) stays put and is honored once onboarding
    // completes; otherwise consume it here so a logged-out deep link
    // (duel/profile/track) survives the OAuth round-trip.
    if (result.success) {
      const u = _user;
      const needsOnboarding =
        result.isNew ||
        !u?.privacy_version_accepted ||
        u?.default_username === true ||
        u?.onboarding_complete === false;
      // Onboarding still owes a return-to consult once it completes — don't
      // consume it here in that case (see Onboarding.svelte submit()).
      navigateReplace(needsOnboarding ? '/onboarding' : consumeReturnTo() || '/duels');
    } else {
      routeOAuthError(result.error || 'TikTok login failed', 'tiktok_login_error');
    }
    return;
  }

  // Handle Google OAuth callback — same shape as TikTok. New users (or anyone
  // who hasn't accepted the current privacy version) are routed to onboarding
  // so the Welcome step can capture DOB + privacy consent.
  if (window.location.pathname === '/auth/google-callback') {
    const result = await handleGoogleCallback();
    if (result.success) {
      const u = _user;
      const needsOnboarding =
        result.isNew ||
        !u?.privacy_version_accepted ||
        u?.default_username === true ||
        u?.onboarding_complete === false;
      // Onboarding still owes a return-to consult once it completes — don't
      // consume it here in that case (see Onboarding.svelte submit()).
      navigateReplace(needsOnboarding ? '/onboarding' : consumeReturnTo() || '/duels');
    } else {
      routeOAuthError(result.error || 'Google login failed', 'google_login_error');
    }
    return;
  }

  // Handle Spotify OAuth callback — same shape as Google.
  if (window.location.pathname === '/auth/spotify-login-callback') {
    const result = await handleSpotifyLoginCallback();
    if (result.success) {
      const u = _user;
      const needsOnboarding =
        result.isNew ||
        !u?.privacy_version_accepted ||
        u?.default_username === true ||
        u?.onboarding_complete === false;
      navigateReplace(needsOnboarding ? '/onboarding' : consumeReturnTo() || '/duels');
    } else {
      routeOAuthError(result.error || 'Spotify login failed', 'spotify_login_error');
    }
    return;
  }

  // Handle Spotify CONNECT callback (authenticated account link, distinct from login).
  if (window.location.pathname === '/auth/spotify-callback') {
    const result = await handleSpotifyConnectCallback();
    const ret = sessionStorage.getItem('spotify_connect_return') || '/account';
    sessionStorage.removeItem('spotify_connect_return');
    if (result.success) {
      sessionStorage.setItem('music_just_connected', 'spotify');
    } else {
      sessionStorage.setItem('spotify_connect_error', result.error || 'Spotify connect failed');
    }
    navigateReplace(ret);
    return;
  }

  // Handle Apple OAuth callback — same shape as Google/Spotify.
  if (window.location.pathname === '/auth/apple-callback') {
    const result = await handleAppleCallback();
    if (result.success) {
      const u = _user;
      const needsOnboarding =
        result.isNew ||
        !u?.privacy_version_accepted ||
        u?.default_username === true ||
        u?.onboarding_complete === false;
      navigateReplace(needsOnboarding ? '/onboarding' : consumeReturnTo() || '/duels');
    } else {
      routeOAuthError(result.error || 'Apple login failed', 'apple_login_error');
    }
    return;
  }

  if (!_token) return;

  try {
    await fetchCurrentUser();
  } catch {
    // Token expired or invalid — clear it
    logout();
  }
}
